import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Crown, Diamond, Sparkles, Shield, Gift } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ScratchCard from '../../components/scratch/ScratchCard';
import confetti from 'canvas-confetti';
import { shopService } from '../../services/shopService';
import { userService } from '../../services/userService';

const ScratchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRevealed, setIsRevealed] = useState(false);
  const [reward, setReward] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExhausted, setIsExhausted] = useState(false);
  const [cardDimensions, setCardDimensions] = useState({ width: 340, height: 480 });

  const shopId = searchParams.get('shopId') || sessionStorage.getItem('currentShopId');
  const userPhone = sessionStorage.getItem('userPhone');

  useEffect(() => {
    // Handle responsive card dimensions
    const updateDimensions = () => {
      const width = window.innerWidth < 400 ? window.innerWidth - 40 : 340;
      const height = (width / 340) * 480;
      setCardDimensions({ width, height });
    };

    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    const fetchReward = async () => {
      if (!shopId || !userPhone) {
        navigate(`/scan?shopId=${shopId || ''}`);
        return;
      }

      try {
        const cachedReward = sessionStorage.getItem('currentReward');
        const shopData = await shopService.getShopById(shopId);
        setShop(shopData);

        if (cachedReward) {
          setReward(JSON.parse(cachedReward));
        } else {
          const activeIdx = sessionStorage.getItem('currentCampaignIdx');
          const selectedReward = await shopService.getRandomReward(shopId, userPhone, activeIdx);
          
          if (selectedReward === null) {
            setIsExhausted(true);
          } else {
            setReward(selectedReward);
            sessionStorage.setItem('currentReward', JSON.stringify(selectedReward));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setReward({ name: 'Surprise Gift', icon: '🎁' });
      } finally {
        setLoading(false);
      }
    };

    fetchReward();
    return () => window.removeEventListener('resize', updateDimensions);
  }, [shopId, userPhone]);

  const handleComplete = async () => {
    setIsRevealed(true);

    const phone = sessionStorage.getItem('userPhone');
    const name = sessionStorage.getItem('userName');
    if (phone && reward && shop) {
      // Save in background to avoid blocking navigation
      userService.saveScratch({
        userPhone: phone,
        userName: name || 'Anonymous',
        shopId: shop.id,
        shopName: shop.shopName,
        reward: reward,
        campaignName: reward.campaignName || 'Standard',
        campaignMode: 'scratch' // explicitly track mode for dashboard accuracy
      }).catch(err => console.error("History save failed:", err));
      
      sessionStorage.removeItem('currentReward');
    }

    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#d4af37', '#fcf6ba', '#b8860b']
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfdfd] flex flex-col items-center justify-center text-black">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="mb-8">
          <Diamond size={40} strokeWidth={1} />
        </motion.div>
        <p className="text-[10px] font-black uppercase tracking-[0.5em] animate-pulse">Calculating your destiny...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-6 bg-[#fdfdfd] overflow-hidden relative">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
      
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 md:top-12 md:left-12 flex items-center gap-3 text-[#999] hover:text-black transition-all text-[10px] font-black uppercase tracking-[0.4em] group z-20"
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        <span className="hidden sm:inline">Return to Portal</span>
      </motion.button>

      <div className="z-10 text-center mb-8 md:mb-16 relative w-full px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-3 mb-6 md:mb-10 text-black bg-black/5 px-6 md:px-8 py-2 md:py-3 rounded-full border border-black/[0.05] shadow-sm relative overflow-hidden"
        >
          <Shield size={14} className="text-[#d4af37]" />
          <span className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.4em]">{shop?.shopName || 'Authentic Protocol Active'}</span>
        </motion.div>
        <h1 className="text-4xl md:text-8xl font-serif text-black tracking-tighter leading-tight italic break-words">
          {isExhausted ? 'Protocol Complete' : (shop?.campaigns?.[sessionStorage.getItem('currentCampaignIdx') || 0]?.name || 'Exclusive Offer')}
        </h1>
        <p className="text-[#999] text-[9px] md:text-[11px] font-bold uppercase tracking-[0.4em] md:tracking-[0.6em] mt-6 md:mt-10 max-w-xs mx-auto">
          {isExhausted ? "You've successfully claimed all rewards at this location." : 
           reward?.name === 'Better luck next time!' ? 'Try again soon' : 'Gently scratch to unveil your elite privilege'}
        </p>
      </div>

      <motion.div
        initial={{ y: 50, scale: 0.9, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className="relative z-10 group"
      >
        <div className="p-1 rounded-[3rem] md:rounded-[4rem] bg-gradient-to-br from-black/10 via-white to-black/10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] relative z-10">
          <div className="bg-white rounded-[2.8rem] md:rounded-[3.8rem] overflow-hidden border border-black/[0.05]">
            {isExhausted ? (
              <div className="flex flex-col items-center justify-center gap-10 py-20 px-12 text-center" style={{ width: cardDimensions.width, height: cardDimensions.height }}>
                <div className="w-24 h-24 rounded-[2rem] bg-black flex items-center justify-center text-[#d4af37] shadow-2xl">
                  <Crown size={40} />
                </div>
                <div className="space-y-4">
                  <h2 className="text-3xl font-serif text-black italic">Thank You!</h2>
                  <p className="text-[10px] font-black text-[#d4af37] uppercase tracking-[0.3em] mb-2">{shop?.shopName}</p>
                  <p className="text-[10px] font-bold text-[#999] uppercase tracking-widest leading-loose max-w-[200px] mx-auto">
                    You have successfully claimed all exclusive rewards. We appreciate your visit!
                  </p>
                </div>
                <button 
                  onClick={() => navigate('/')}
                  className="luxury-button !px-10 !py-4"
                >
                  Return to Portal
                </button>
              </div>
            ) : !reward || reward.name === 'Better luck next time!' ? (
              <div className="flex flex-col items-center justify-center gap-6 py-20 px-12 text-center" style={{ width: cardDimensions.width, height: cardDimensions.height }}>
                <div className="w-16 h-16 rounded-full bg-black/5 flex items-center justify-center text-[#ccc] mb-4">
                  <Gift size={28} />
                </div>
                <h2 className="text-xl font-serif text-black">No Active Rewards</h2>
                <p className="text-[9px] font-bold text-[#999] uppercase tracking-widest leading-loose">The administrator has not configured rewards for this node yet.</p>
              </div>
            ) : (
              <ScratchCard 
                onComplete={handleComplete}
                width={cardDimensions.width}
                height={cardDimensions.height}
                overlayColor="#111111"
                finishPercent={50}
                type={shop?.campaigns?.[shop.activeCampaignIdx || 0]?.type || 'Elite Golden Card (Standard)'}
              >
                <div className="flex flex-col items-center gap-8 md:gap-12 py-10 md:py-16 px-6 md:px-8 h-full justify-center">
                  <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative">
                    <div className="absolute inset-0 bg-[#d4af37]/20 blur-[40px] md:blur-[60px] rounded-full scale-150" />
                    <div className="text-7xl md:text-9xl relative drop-shadow-2xl">{reward?.icon || '🎁'}</div>
                    <Sparkles className="absolute -top-4 -right-4 text-[#d4af37] animate-bounce" size={20} />
                  </motion.div>
                  
                  <div className="space-y-4 md:space-y-6 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-emerald-600 text-[8px] md:text-[10px] font-black uppercase tracking-[0.4em] flex items-center gap-2">
                        <div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
                        Verification Success
                      </span>
                      <h2 className="text-2xl md:text-4xl font-serif text-black leading-tight px-2">{reward?.name}</h2>
                    </div>
                    <div className="w-10 h-[1px] bg-black/10 mx-auto" />
                    <p className="text-[#999] text-[8px] md:text-[9px] font-bold uppercase tracking-[0.4em] leading-relaxed max-w-[180px] mx-auto">Your digital asset is ready for deployment.</p>
                  </div>
                  
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: isRevealed ? 1 : 0, y: isRevealed ? 0 : 10 }}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => {
                      sessionStorage.removeItem('currentReward');
                      sessionStorage.removeItem('hasScratched');
                      navigate('/reward');
                    }}
                    className="luxury-button !px-8 md:!px-12 !py-4 md:!py-5 shadow-2xl whitespace-nowrap flex items-center justify-center min-w-[200px] md:min-w-[240px]"
                  >
                    SECURE REWARD
                  </motion.button>
                </div>
              </ScratchCard>
            )}
          </div>
        </div>
      </motion.div>

      <div className="mt-12 md:mt-24 flex items-center gap-8 md:gap-12 opacity-10">
        <div className="h-[1px] w-20 md:w-32 bg-black" />
        <Crown size={18} className="text-black" />
        <div className="h-[1px] w-20 md:w-32 bg-black" />
      </div>
    </div>
  );
};

export default ScratchPage;
